import { useState } from "react";
import { Button, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { InconsistentReferenceTitle } from "../../../../../../../../../components/Common/InconsistentReferenceTooltip/InconsistentReference";
import { TransactionInfoApi } from "../../../../../../../../../services/Api/Organization/types";
import {
	TransactionInfoExpandState,
	getTransactionInfoFormattedValue,
	getTransactionInfosToDisplay,
} from "../utils/utils";

interface Props {
	inconsistentReferenceId: boolean;
	infos: TransactionInfoApi[];
	currency: string;
}

const TransactionInfosCard = ({ inconsistentReferenceId, infos, currency }: Props) => {
	const [expandState, setExpandState] = useState<TransactionInfoExpandState>(() =>
		infos.length < 5 ? TransactionInfoExpandState.INACTIVE : TransactionInfoExpandState.HIDDEN
	);
	const infosToDisplay = getTransactionInfosToDisplay(infos, expandState);
	const displayExpandButton = expandState === TransactionInfoExpandState.HIDDEN;
	const displayHideButton = expandState === TransactionInfoExpandState.EXPANDED;
	const detailItemClasses = "text-break mw-50";
	const { t } = useTranslation();

	const drawDataElement = (type: string, data: string) => {
		const dataElementClasses = `${detailItemClasses} font-weight-bold`;

		if (type === "PUBLIC_LINK") {
			return (
				<a href={data} target="_blank" rel="noreferrer" className={dataElementClasses}>
					{data}
				</a>
			);
		}

		return <span className={dataElementClasses}>{getTransactionInfoFormattedValue(type, data, currency)}</span>;
	};

	const handleExpandInfosButtonClick = () => {
		setExpandState((prevExpandState) =>
			prevExpandState === TransactionInfoExpandState.HIDDEN
				? TransactionInfoExpandState.EXPANDED
				: TransactionInfoExpandState.HIDDEN
		);
	};

	return (
		<Card>
			<Card.Body>
				<InconsistentReferenceTitle
					inconsistentReferenceId={inconsistentReferenceId}
					titleText={t("common.word.details")}
				/>
				<div className="d-flex row-gap-2 flex-column">
					{infosToDisplay.map(({ type, data }) => {
						return (
							<div key={`${type}_${data}`} className="d-flex justify-content-between gap-1">
								<span className={detailItemClasses}>{`${t(
									`enums.transaction_info.type.${type}`
								)}:`}</span>
								{drawDataElement(type, data)}
							</div>
						);
					})}
				</div>
				{(displayExpandButton || displayHideButton) && (
					<Button variant="add" onClick={handleExpandInfosButtonClick} className="w-100 mt-4 mb-0">
						{t(`common.action.${displayExpandButton ? "more" : "less"}`, { ns: "lib" })}
					</Button>
				)}
			</Card.Body>
		</Card>
	);
};

export default TransactionInfosCard;

// t("enums.transaction_info.type.PUBLIC_LINK")
// t("enums.transaction_info.type.TAX_ID_NO")
// t("enums.transaction_info.type.TIP_AMOUNT")
// t("enums.transaction_info.type.EMAIL")
// t("enums.transaction_info.type.AID")
// t("enums.transaction_info.type.AUTHORIZATION_TYPE")
// t("enums.transaction_info.type.ADDRESS_LINE_1")
// t("enums.transaction_info.type.ADDRESS_LINE_2")
// t("enums.transaction_info.type.ADDRESS_LINE_3")
// t("enums.transaction_info.type.TERMINAL_PRINTING_INDICATOR")
// t("enums.transaction_info.type.AUTHORIZATION_CODE")
// t("enums.transaction_info.type.TC")
// t("enums.transaction_info.type.CARD_READ_TYPE")
// t("enums.transaction_info.type.MASKED_CARD_NUMBER")
// t("enums.transaction_info.type.BILL_NUMBER")
// t("enums.transaction_info.type.MID")
// t("enums.transaction_info.type.DATE")
// t("enums.transaction_info.type.AUTHORIZATION_MESSAGE_NUMBER")
// t("enums.transaction_info.type.EMV_CRYPTOGRAM_TYPE")
// t("enums.transaction_info.type.PRINTOUT_TYPE")
// t("enums.transaction_info.type.TSI")
// t("enums.transaction_info.type.TRANSACTION_AMOUNT")
// t("enums.transaction_info.type.TVR")
// t("enums.transaction_info.type.PAYMENT_TERMINAL_TRANSACTION_TYPE")
// t("enums.transaction_info.type.TRANSACTION_CURRENCY")
// t("enums.transaction_info.type.TID")
// t("enums.transaction_info.type.PRINTOUT_CONTEXT")
// t("enums.transaction_info.type.TRANSACTION_NUMBER")
// t("enums.transaction_info.type.BRAND_NAME_OF_THE_CARD")
// t("enums.transaction_info.type.ATC")
// t("enums.transaction_info.type.ADDITIONAL_TRANSACTION_FLAG")
// t("enums.transaction_info.type.CARDHOLDER_AUTHORIZATION_METHOD")
